import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Users,
  Clock,
  FileText,
  RefreshCw
} from 'lucide-react';
import { TakaIcon } from '../../components/UI';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import * as XLSX from 'xlsx'; // For export if needed
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface Customer {
  _id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  company?: string;
  contactPerson?: string;
  notes?: string;
}

interface Project {
  _id: string;
  name: string;
  description?: string;
  customerName: string;
  startDate: string;
  endDate?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  actualCost: number;
  teamMembers: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    role: string;
  }>;
  location?: string;
  notes?: string;
  invoices: Array<{
    _id: string;
    invoiceNumber: string;
    totalAmount: number;
    status: string;
  }>;
  createdAt: string;
  updatedAt: string;
  workOrders?: Array<{
    number: string;
    date: string;
  }>;
  projectAddress?: string;
}

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    customerName: '',
    startDate: '',
    workOrders: [{ number: '', date: '' }],
    projectAddress: '',
    status: 'planning',
  });
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [workOrderProjectId, setWorkOrderProjectId] = useState<string | null>(null);
  const [newWorkOrder, setNewWorkOrder] = useState({ number: '', date: '' });
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [viewProject, setViewProject] = useState<any>(null);
  const [viewCustomer, setViewCustomer] = useState<any>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const { settings } = useSettings();
  const [projectInvoiceData, setProjectInvoiceData] = useState<any[]>([]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        // Clear cache on component mount to ensure fresh data
        apiService.invalidateProjectsCache();
        await fetchProjects(true);
        await fetchCustomers();
        await fetchProjectInvoiceData();
      } catch (error) {
        toast.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const fetchProjectInvoiceData = async () => {
    try {
      const response = await apiService.get('/reports/project-based');
      setProjectInvoiceData(response.data.projectInvoiceData || []);
    } catch (error) {
    }
  };

  // Fetch project and customer for modal view
  useEffect(() => {
    if (!selectedProjectId) return;
    setViewLoading(true);
    setViewError('');
    setViewProject(null);
    setViewCustomer(null);
    (async () => {
      try {
        const res = await apiService.get(`/projects/${selectedProjectId}`);
        setViewProject(res.data);
        const customersRes = await apiService.get('/customers');
        const customers = customersRes.data.customers || [];
        const found = customers.find((c: any) => c.name === res.data.customerName);
        setViewCustomer(found || null);
      } catch (err) {
        setViewError('Failed to load project details.');
      } finally {
        setViewLoading(false);
      }
    })();
  }, [selectedProjectId]);

  const fetchProjects = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      let response;
      if (forceRefresh) {
        // For force refresh, bypass cache completely
        response = await apiService.forceRefresh('/projects?limit=1000'); // Request all projects
      } else {
        response = await apiService.get('/projects?limit=1000'); // Request all projects
      }
      
      setProjects(response.data.projects || []);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Soft refresh listener
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateProjectsCache();
      fetchProjects(true);
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await apiService.get('/customers');
      const customersData = response.data.customers || [];
      setCustomers(customersData);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const response = await apiService.delete(`/projects/${projectId}`);
        toast.success('Project deleted successfully');
        
        // Immediately clear projects cache and force refresh
        apiService.invalidateProjectsCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchProjects(true);
        }, 100);
      } catch (error) {
        toast.error('Failed to delete project');
      }
    }
  };

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      const response = await apiService.patch(`/projects/${projectId}`, { status: newStatus });
      toast.success('Project status updated successfully');
      
      // Immediately clear projects cache and force refresh
      apiService.invalidateProjectsCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchProjects(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to update project status');
    }
  };

  const handleCreateProject = async () => {
    try {
      // Only include workOrders if BOTH number and date are provided
      const workOrders = (newProject.workOrders && newProject.workOrders[0].number && newProject.workOrders[0].date)
        ? [{ number: newProject.workOrders[0].number, date: newProject.workOrders[0].date }]
        : [];
      
      const payload = {
        ...newProject,
        workOrders,
      };
      const response = await apiService.post('/projects', payload);
      toast.success('Project created successfully');
      apiService.invalidateProjectsCache();
      setShowNewProjectModal(false);
      setNewProject({ name: '', customerName: '', startDate: '', workOrders: [{ number: '', date: '' }], projectAddress: '', status: 'planning' });
      setTimeout(async () => {
        await fetchProjects(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || project.status === selectedStatus;
    const matchesCustomer = !selectedCustomer || project.customerName === selectedCustomer;
    
    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      const projectDate = new Date(project.startDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      matchesDate = projectDate >= startDate && projectDate <= endDate;
    }
    
    return matchesSearch && matchesStatus && matchesCustomer && matchesDate;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'on-hold': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressPercentage = (project: Project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'cancelled') return 0;
    
    const startDate = new Date(project.startDate);
    const endDate = project.endDate ? new Date(project.endDate) : new Date();
    const today = new Date();
    
    if (today < startDate) return 0;
    if (today > endDate) return 100;
    
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    return Math.round((elapsed / totalDuration) * 100);
  };

  const exportProjects = async () => {
    try {
      await apiService.download('/projects/export', 'projects.xlsx');
      toast.success('Projects exported successfully');
    } catch (error) {
      toast.error('Failed to export projects');
    }
  };

  const handleOpenWorkOrderModal = (projectId: string) => {
    setWorkOrderProjectId(projectId);
    setNewWorkOrder({ number: '', date: '' });
    setShowWorkOrderModal(true);
  };

  const handleAddWorkOrder = async () => {
    if (!workOrderProjectId) return;
    try {
      await apiService.patch(`/projects/${workOrderProjectId}/add-work-order`, newWorkOrder);
      toast.success('Work order added');
      setShowWorkOrderModal(false);
      setWorkOrderProjectId(null);
      setNewWorkOrder({ number: '', date: '' });
      apiService.invalidateProjectsCache();
      setTimeout(async () => {
        await fetchProjects(true);
      }, 100);
    } catch (e) {
      toast.error('Failed to add work order');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-4 md:p-6 font-[var(--font-family)] text-[var(--color-text)] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--color-text)] font-[var(--font-family)]">Projects</h1>
          <p className="text-[var(--color-text)] font-[var(--font-family)] text-sm md:text-base">
            Track and manage your projects
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
          <button
            onClick={() => {
              apiService.invalidateProjectsCache();
              fetchProjects(true);
              fetchProjectInvoiceData();
              toast.success('Cache cleared and data refreshed');
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={exportProjects}
            className="flex items-center px-2 md:px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors font-['Times_New_Roman'] text-xs md:text-sm"
            style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
          >
            <Download className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center px-2 md:px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors font-['Times_New_Roman'] text-xs md:text-sm"
            style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
          >
            <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Print</span>
          </button>
          <Button
            variant="primary"
            onClick={() => setShowNewProjectModal(true)}
            icon={<Plus className="w-4 h-4 md:w-5 md:h-5" />}
            className="text-xs md:text-sm"
          >
            <span className="hidden sm:inline">Add Project</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4 md:p-6 mb-6" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex flex-col gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-4 h-4" />
              <input
                type="text"
                placeholder="Search projects by name, client, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)] text-sm"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors font-['Times_New_Roman'] text-sm"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4"
          >
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] text-sm"
            >
              <option value="">All Status</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] text-sm"
            >
              <option value="">All Customers</option>
              {customers && customers.length > 0 ? (
                customers.map(customer => (
                  <option key={customer._id} value={customer.name}>
                    {customer.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>Loading customers...</option>
              )}
            </select>

            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] text-sm"
            />

            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] text-sm"
            />
          </motion.div>
        )}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {filteredProjects.map((project) => {
          const progress = getProgressPercentage(project);
          const projectInvoiceItem = projectInvoiceData.find(data => data.projectId === project._id);
          const totalDeliveryInvoiced = projectInvoiceItem?.totalDeliveryInvoiced || 0;
          const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

          return (
            <motion.div
              key={project._id}
              initial={{ opacity: 0, y: 20 } as any}
              animate={{ opacity: 1, y: 0 } as any}
              exit={{ opacity: 0, y: -20 } as any}
              className="bg-[var(--color-background)] rounded-lg shadow-md overflow-hidden border border-[var(--color-border)]"
              style={{ borderRadius: 'var(--border-radius)' } as any}
            >
              {/* Project Header */}
              <div className="p-4 md:p-6 border-b border-[var(--color-border)]">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[var(--color-text)] font-['Times_New_Roman'] text-sm md:text-base truncate">
                      {project.name}
                    </h3>
                    <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman'] text-xs md:text-sm truncate">
                      Customer: {project.customerName}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    <select
                      value={project.status}
                      onChange={(e) => handleStatusChange(project._id, e.target.value)}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-['Times_New_Roman'] border-0 focus:ring-2 focus:ring-[var(--color-primary)]`}
                      style={{ 
                        borderRadius: 'var(--border-radius)', 
                        background: getStatusColor(project.status), 
                        color: 'white' 
                      } as unknown as React.CSSProperties}
                    >
                      <option value="planning">Planning</option>
                      <option value="active">Active</option>
                      <option value="on-hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Project Details */}
                <div className="space-y-3">
                  {/* Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs md:text-sm font-['Times_New_Roman']">
                      <span className="text-[var(--color-text-secondary)]">Progress</span>
                      <span className="font-medium text-[var(--color-text)]">{progress}%</span>
                    </div>
                    <div className="mt-1 w-full bg-[var(--color-border)] rounded-full h-2">
                      <div
                        className="bg-[var(--color-primary)] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` } as unknown as React.CSSProperties}
                      ></div>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="grid grid-cols-1 gap-3 text-xs md:text-sm font-['Times_New_Roman']">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-[var(--color-text-secondary)]">
                        <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                        <span>Start Date</span>
                      </div>
                      <p className="font-medium text-[var(--color-text)] text-right">
                        {new Date(project.startDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-[var(--color-text-secondary)]">
                        <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                        <span>End Date</span>
                      </div>
                      <p className="font-medium text-[var(--color-text)] text-right">
                        {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'TBD'}
                      </p>
                    </div>
                  </div>

                  {/* Invoiced Info */}
                  <div className="flex items-center justify-between text-xs md:text-sm font-['Times_New_Roman']">
                    <div className="flex items-center text-[var(--color-text-secondary)]">
                      <TakaIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                      <span>Invoiced</span>
                    </div>
                    <p className="font-medium text-[var(--color-text)]">
                      {typeof totalDeliveryInvoiced === 'number' ? `${currencySymbol}${totalDeliveryInvoiced.toLocaleString()}` : 'N/A'}
                    </p>
                  </div>

                  {/* Team Members */}
                  <div>
                    <div className="flex items-center text-[var(--color-text-secondary)] text-xs md:text-sm font-['Times_New_Roman'] mb-2">
                      <Users className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                      <span>Team ({(project.teamMembers ? project.teamMembers.length : 0)})</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(project.teamMembers || []).slice(0, 2).map((member, index) => (
                        <span
                          key={member.user._id}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-[var(--color-primary-light)] text-[var(--color-primary)] font-['Times_New_Roman']"
                        >
                          {member.user.firstName} {member.user.lastName}
                        </span>
                      ))}
                      {(project.teamMembers && project.teamMembers.length > 2) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-[var(--color-border)] text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                          +{project.teamMembers.length - 2} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-[var(--color-border)] mt-4">
                  <div className="flex space-x-1 md:space-x-2">
                    <button
                      className="p-1.5 md:p-2 text-[var(--color-primary)] dark:text-[var(--color-primary-dark)] hover:bg-[var(--color-primary-light)] dark:hover:bg-[var(--color-primary-dark)] rounded-lg transition-colors"
                      title="View Details"
                      onClick={() => setSelectedProjectId(project._id)}
                    >
                      <Eye className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                    <button
                      className="p-1.5 md:p-2 text-[var(--color-success)] dark:text-[var(--color-success-dark)] hover:bg-[var(--color-success-light)] dark:hover:bg-[var(--color-success-dark)] rounded-lg transition-colors"
                      title="Edit Project"
                      onClick={() => navigate(`/projects/${project._id}/edit`)}
                    >
                      <Edit className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                    <button
                      className="p-1.5 md:p-2 text-[var(--color-warning)] hover:bg-[var(--color-warning-light)] rounded-lg transition-colors text-xs"
                      title="Add Work Order"
                      onClick={() => handleOpenWorkOrderModal(project._id)}
                    >
                      <span className="hidden sm:inline">+ Work Order</span>
                      <span className="sm:hidden">+ WO</span>
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteProject(project._id)}
                    className="p-1.5 md:p-2 text-[var(--color-danger)] dark:text-[var(--color-danger-dark)] hover:bg-[var(--color-danger-light)] dark:hover:bg-[var(--color-danger-dark)] rounded-lg transition-colors"
                    title="Delete Project"
                  >
                    <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
            No projects found
          </h3>
          <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
            {searchTerm || selectedStatus || dateRange.start
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first project'
            }
          </p>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman'] mt-6">
        Showing {filteredProjects.length} of {projects.length} projects
      </div>

      <Modal isOpen={showNewProjectModal} onClose={() => setShowNewProjectModal(false)} title="New Project" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input label="Project Name" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-2">Customer Name</label>
            <select
              value={newProject.customerName}
              onChange={e => setNewProject({ ...newProject, customerName: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              required
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer._id} value={customer.name}>{customer.name}</option>
              ))}
            </select>
          </div>
          <Input label="Work Order Number" value={newProject.workOrders[0].number} onChange={e => setNewProject({ ...newProject, workOrders: [{ ...newProject.workOrders[0], number: e.target.value }] })} />
          <Input label="Work Order Date" type="date" value={newProject.workOrders[0].date} onChange={e => setNewProject({ ...newProject, workOrders: [{ ...newProject.workOrders[0], date: e.target.value }] })} />
          <Input label="Project Address" value={newProject.projectAddress} onChange={e => setNewProject({ ...newProject, projectAddress: e.target.value })} />
          <Input label="Start Date" type="date" value={newProject.startDate} onChange={e => setNewProject({ ...newProject, startDate: e.target.value })} />
          <select value={newProject.status} onChange={e => setNewProject({ ...newProject, status: e.target.value })} className="input">
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on-hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button className="btn btn-primary w-full" onClick={handleCreateProject}>Create Project</button>
        </div>
      </Modal>

      <Modal isOpen={showWorkOrderModal} onClose={() => setShowWorkOrderModal(false)} title="Add Work Order" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input label="Work Order Number" value={newWorkOrder.number} onChange={e => setNewWorkOrder({ ...newWorkOrder, number: e.target.value })} />
          <Input label="Work Order Date" type="date" value={newWorkOrder.date} onChange={e => setNewWorkOrder({ ...newWorkOrder, date: e.target.value })} />
          <button className="btn btn-primary w-full" onClick={handleAddWorkOrder}>Add Work Order</button>
        </div>
      </Modal>

      {/* Project Details Modal */}
      <Modal
        isOpen={!!selectedProjectId}
        onClose={() => { setSelectedProjectId(null); setViewProject(null); setViewCustomer(null); setViewError(''); }}
        title={viewProject ? viewProject.name : 'Project Details'}
        size="lg"
        showCloseButton
        className="z-[10000]"
      >
        {viewLoading ? (
          <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
        ) : viewError ? (
          <div className="text-center text-[var(--color-text)] p-8 font-semibold font-['Times_New_Roman']">{viewError}</div>
        ) : !viewProject ? (
          <div className="text-center text-[var(--color-text)] p-8 font-semibold font-['Times_New_Roman']">Project not found.</div>
        ) : (
          <div className="max-w-3xl mx-auto py-2 px-2 font-['Times_New_Roman']">
            <div className="mb-6">
              <h2 className="text-3xl font-bold mb-2 text-[var(--color-text)]">{viewProject.name}</h2>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-surface-light)] text-[var(--color-text-secondary)] mb-2">
                Status: {viewProject.status.charAt(0).toUpperCase() + viewProject.status.slice(1)}
              </span>
              <p className="mb-2 text-[var(--color-text)]">{viewProject.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Clock className="w-4 h-4 mr-2" />Start Date:</div>
                <div className="font-medium text-[var(--color-text)]">{new Date(viewProject.startDate).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Clock className="w-4 h-4 mr-2" />End Date:</div>
                <div className="font-medium text-[var(--color-text)]">{viewProject.endDate ? new Date(viewProject.endDate).toLocaleDateString() : 'TBD'}</div>
              </div>
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Users className="w-4 h-4 mr-2" />Customer Name:</div>
                <div className="font-medium text-[var(--color-text)]">{viewCustomer?.name || viewProject.customerName || 'N/A'}</div>
              </div>
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Users className="w-4 h-4 mr-2" />Customer Email:</div>
                <div className="font-medium text-[var(--color-text)]">{viewCustomer?.email || 'N/A'}</div>
              </div>
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Users className="w-4 h-4 mr-2" />Customer Phone:</div>
                <div className="font-medium text-[var(--color-text)]">{viewCustomer?.phone || 'N/A'}</div>
              </div>
              <div>
                <div className="flex items-center mb-2 text-[var(--color-text-secondary)]"><Users className="w-4 h-4 mr-2" />Customer Address:</div>
                <div className="font-medium text-[var(--color-text)]">{viewCustomer?.address || 'N/A'}</div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text)]">Team Members</h3>
              <div className="flex flex-wrap gap-2">
                {(viewProject.teamMembers || []).length === 0 && <span className="text-[var(--color-text)]">No team members.</span>}
                {(viewProject.teamMembers || []).map((member: any) => (
                  <span key={member.user._id} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-[var(--color-surface-light)] text-[var(--color-text-secondary)]">
                    {member.user.firstName} {member.user.lastName} <span className="ml-2 text-[var(--color-text)]">({member.role})</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text)]">Invoices</h3>
              <DoubleScrollbar>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--color-surface-light)]">
                      <th className="px-4 py-2 text-left text-[var(--color-text-secondary)]">Invoice #</th>
                      <th className="px-4 py-2 text-left text-[var(--color-text-secondary)]">Amount</th>
                      <th className="px-4 py-2 text-left text-[var(--color-text-secondary)]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewProject.invoices || []).length === 0 && (
                      <tr><td colSpan={3} className="text-center py-2 text-[var(--color-text)]">No invoices.</td></tr>
                    )}
                    {(viewProject.invoices || []).map((inv: any) => (
                      <tr key={inv._id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-light)] transition-colors cursor-pointer">
                        <td className="px-4 py-2 text-[var(--color-text)]">
                          <a href={`/invoices/${inv._id}`} className="text-[var(--color-primary)] underline" target="_blank" rel="noopener noreferrer">
                            {inv.invoiceNumber}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-[var(--color-text)]">৳{inv.totalAmount?.toLocaleString() ?? 'N/A'}</td>
                        <td className="px-4 py-2 text-[var(--color-text)]">{inv.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DoubleScrollbar>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text)]">Work Orders</h3>
              {viewProject.workOrders && viewProject.workOrders.length > 0 ? (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--color-surface-light)]">
                      <th className="px-4 py-2 text-left text-[var(--color-text-secondary)]">Work Order Number</th>
                      <th className="px-4 py-2 text-left text-[var(--color-text-secondary)]">Work Order Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewProject.workOrders.map((wo: any, idx: number) => (
                      <tr key={idx} className="border-b border-[var(--color-border)]">
                        <td className="px-4 py-2 text-[var(--color-text)]">{wo.number}</td>
                        <td className="px-4 py-2 text-[var(--color-text)]">{wo.date ? new Date(wo.date).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-[var(--color-text)]">No work orders.</div>
              )}
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text)]">Other Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-bold text-[var(--color-text)]">Location:</span> <span className="text-[var(--color-text)]">{
                    typeof viewProject.location === 'string'
                      ? viewProject.location
                      : viewProject.location && typeof viewProject.location === 'object'
                        ? ((loc: any) => [loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean).join(', '))(viewProject.location)
                        : 'N/A'
                  }</span>
                </div>
                <div><span className="font-bold text-[var(--color-text)]">Notes:</span> <span className="text-[var(--color-text)]">{viewProject.notes || 'N/A'}</span></div>
                <div><span className="font-bold text-[var(--color-text)]">Created At:</span> <span className="text-[var(--color-text)]">{new Date(viewProject.createdAt).toLocaleString()}</span></div>
                <div><span className="font-bold text-[var(--color-text)]">Updated At:</span> <span className="text-[var(--color-text)]">{new Date(viewProject.updatedAt).toLocaleString()}</span></div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProjectsPage; 